import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Paper,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TableSortLabel,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  Grid,
  InputLabel,
  FormControl
} from "@material-ui/core";
import api from "../api";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  title: { marginBottom: theme.spacing(3) },
  paper: { padding: theme.spacing(3) },
  tableContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  rentAmount: {
    fontSize: "1.5rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius,
    display: "inline-block"
  },
  confidenceChip: { minWidth: 80 },
  highConfidence: {
    backgroundColor: "#4caf50",
    color: "white"
  },
  mediumConfidence: {
    backgroundColor: "#ff9800",
    color: "white"
  },
  lowConfidence: {
    backgroundColor: "#f44336",
    color: "white"
  },
  filterContainer: { marginBottom: theme.spacing(3) },
  formControl: { minWidth: 200 },
  rangeDisplay: { marginTop: theme.spacing(1) },
  summaryBox: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100]
  }
}));

// Array of percent options for the combo box (0-100% with steps of 10%)
const percentOptions = [
  0,
  ...Array.from({ length: 10 }, (_, i) => (i + 1) * 10)
];

// Debounce function to prevent multiple rapid requests
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);

  return useCallback(
    (...args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );
}

export default function PmsTransactionsPage({
  customerId,
  defaultPmsOnly = true
}) {
  const classes = useStyles();
  const params = useParams();
  // Use customerId from props if provided, otherwise get from URL
  const id = customerId || params.id;

  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [transactionsData, setTransactionsData] = useState(null);
  const [sortedTransactions, setSortedTransactions] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("date");
  const [rentAmount, setRentAmount] = useState(null);

  // For tracking the latest request
  const requestIdRef = useRef(0);

  // Filters - use defaultPmsOnly as initial value
  const [pmsOnly, setPmsOnly] = useState(defaultPmsOnly);
  const [percentRange, setPercentRange] = useState(10);

  // Fetch transactions with the current filters
  const fetchTransactions = useCallback(
    async (pmsOnlyVal, percentRangeVal) => {
      setLoading(true);
      // Increment request ID to track the latest request
      const currentRequestId = ++requestIdRef.current;

      try {
        let params = {
          id,
          pms_only: pmsOnlyVal
        };

        // Only add amount filters if PMS is not enabled and percent range is set
        if (!pmsOnlyVal && percentRangeVal > 0) {
          const percent = percentRangeVal / 100;

          // If we already have a rent amount from previous requests, use it
          // Otherwise use an approximation for the initial request
          const currentRentAmount = rentAmount || 10000;

          params.amount_min = Math.floor(currentRentAmount * (1 - percent));
          params.amount_max = Math.ceil(currentRentAmount * (1 + percent));
        }

        const response = await api.getCustomerTransactions(params);

        // Only update state if this is the most recent request
        if (currentRequestId === requestIdRef.current) {
          // Store the rent amount for future use
          if (response.data.rentAmount)
            setRentAmount(parseFloat(response.data.rentAmount));

          setTransactionsData(response.data);
          setError(null);
        }
      } catch (err) {
        // Only update error state if this is the most recent request
        if (currentRequestId === requestIdRef.current)
          setError(
            err.response?.data?.error || "Failed to load transactions data"
          );
      } finally {
        // Only update loading state if this is the most recent request
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
          setInitialLoad(false);
        }
      }
    },
    [id, rentAmount]
  );

  // Debounced version of fetchTransactions to prevent rapid multiple requests
  const debouncedFetchTransactions = useDebounce(fetchTransactions, 300);

  // Initial data loading on component mount
  useEffect(() => {
    fetchTransactions(defaultPmsOnly, percentRange);
  }, [id, fetchTransactions]); // Re-run if ID changes or fetchTransactions changes

  // Apply filters when parameters change
  useEffect(() => {
    if (initialLoad) return;
    debouncedFetchTransactions(pmsOnly, percentRange);
  }, [pmsOnly, percentRange, debouncedFetchTransactions, initialLoad]);

  // Sort data when orderBy, order or data changes
  useEffect(() => {
    if (transactionsData && transactionsData.transactions) {
      const sortedData = [...transactionsData.transactions].sort((a, b) => {
        let aValue, bValue;

        switch (orderBy) {
          case "date":
            aValue = new Date(a.date).getTime();
            bValue = new Date(b.date).getTime();
            break;
          case "description":
            aValue = a.description?.toLowerCase() || "";
            bValue = b.description?.toLowerCase() || "";
            break;
          case "amount":
            aValue = a.amount || 0;
            bValue = b.amount || 0;
            break;
          case "confidence":
            aValue = a.binaryConfidence || 0;
            bValue = b.binaryConfidence || 0;
            break;
          default:
            aValue = a[orderBy] || "";
            bValue = b[orderBy] || "";
        }

        if (order === "asc")
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        else return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
      });

      setSortedTransactions(sortedData);
    }
  }, [transactionsData, order, orderBy]);

  const getConfidenceClass = (confidence) => {
    if (confidence === undefined || confidence === null) return "";
    if (confidence >= 0.7) return classes.highConfidence;
    if (confidence >= 0.5) return classes.mediumConfidence;
    return classes.lowConfidence;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const handleSortRequest = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handlePmsOnlyChange = (event) => {
    setPmsOnly(event.target.checked);
  };

  const handlePercentRangeChange = (event) => {
    setPercentRange(parseInt(event.target.value));
  };

  if (initialLoad)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Box className={classes.root}>
        <Typography variant="h5" color="error">
          Error:{" "}
          {error && typeof error === "string" ? error : "An error occurred"}
        </Typography>
      </Box>
    );

  // Calculate amount range (only if percent > 0)
  let amountMin = 0;
  let amountMax = 0;

  if (percentRange > 0 && (rentAmount || transactionsData?.rentAmount)) {
    // Use our stored rentAmount state if available, otherwise use it from transactionsData
    const currentRentAmount =
      rentAmount || parseFloat(transactionsData.rentAmount);
    const percent = percentRange / 100;
    amountMin = Math.floor(currentRentAmount * (1 - percent));
    amountMax = Math.ceil(currentRentAmount * (1 + percent));
  }

  return (
    <Box className={classes.root}>
      <Typography variant="h4" className={classes.title}>
        Transactions for Customer #{id}
      </Typography>

      {(rentAmount > 0 ||
        (transactionsData && transactionsData.rentAmount > 0)) && (
        <Typography variant="h6" component="div" className={classes.rentAmount}>
          Rent Amount: $
          {(rentAmount || parseFloat(transactionsData.rentAmount)).toFixed(2)}
        </Typography>
      )}

      {/* Filters */}
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2} className={classes.filterContainer}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={pmsOnly}
                  onChange={handlePmsOnlyChange}
                  color="primary"
                  disabled={loading}
                />
              }
              label="PMS Transactions Only"
            />
          </Grid>

          {(rentAmount > 0 || transactionsData?.rentAmount > 0) && !pmsOnly && (
            <Grid item xs={12} sm={6}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="percent-range-label">
                  Amount Range (±%)
                </InputLabel>
                <Select
                  labelId="percent-range-label"
                  id="percent-range-select"
                  value={percentRange}
                  onChange={handlePercentRangeChange}
                  label="Amount Range (±%)"
                  disabled={loading}
                >
                  <MenuItem value={0}>No filter</MenuItem>
                  {percentOptions
                    .filter((p) => p > 0)
                    .map((percent) => (
                      <MenuItem key={percent} value={percent}>
                        ±{percent}%
                      </MenuItem>
                    ))}
                </Select>
                {percentRange > 0 && (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.rangeDisplay}
                  >
                    Range: ${amountMin} - ${amountMax}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper className={classes.paper} style={{ marginTop: "16px" }}>
        <Typography variant="h6">Transactions Details</Typography>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="100px"
          >
            <CircularProgress size={30} />
          </Box>
        ) : (
          <>
            {/* Summary information */}
            <Box className={classes.summaryBox}>
              <Typography variant="body1">
                <strong>Total Transactions:</strong>{" "}
                {transactionsData?.totalCount || 0}
              </Typography>
            </Box>

            {transactionsData?.transactions &&
            transactionsData.transactions.length > 0 ? (
              <TableContainer
                component={Paper}
                className={classes.tableContainer}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "date"}
                          direction={orderBy === "date" ? order : "asc"}
                          onClick={() => handleSortRequest("date")}
                        >
                          Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === "description"}
                          direction={orderBy === "description" ? order : "asc"}
                          onClick={() => handleSortRequest("description")}
                        >
                          Description
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === "amount"}
                          direction={orderBy === "amount" ? order : "asc"}
                          onClick={() => handleSortRequest("amount")}
                        >
                          Amount
                        </TableSortLabel>
                      </TableCell>
                      {pmsOnly && (
                        <TableCell align="center">
                          <TableSortLabel
                            active={orderBy === "confidence"}
                            direction={orderBy === "confidence" ? order : "asc"}
                            onClick={() => handleSortRequest("confidence")}
                          >
                            Confidence
                          </TableSortLabel>
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedTransactions.map((transaction, index) => (
                      <TableRow key={index}>
                        <TableCell>{formatDate(transaction.date)}</TableCell>
                        <TableCell>{transaction.description || ""}</TableCell>
                        <TableCell align="right">
                          $
                          {transaction.amount
                            ? transaction.amount.toFixed(2)
                            : "0.00"}
                        </TableCell>
                        {pmsOnly && (
                          <TableCell align="center">
                            {transaction.binaryConfidence !== undefined && (
                              <Chip
                                label={`${(
                                  transaction.binaryConfidence * 100
                                ).toFixed(0)}%`}
                                className={`${
                                  classes.confidenceChip
                                } ${getConfidenceClass(
                                  transaction.binaryConfidence
                                )}`}
                              />
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography>
                No transactions found matching your filters
              </Typography>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
